import { floatify } from 'lib/maths/mathUtils'

export interface LoyaltyMarginTolerance {
  itemId: string
  /** Encrypted margin the customer's points were quoted on 🔑 */
  mx: string
  /**
   * What keeping the quoted points price costs LE over the new one, in the checkout currency; 0
   * when it isn't higher
   */
  cost: number
}

/**
 * Loyalty: a price hold on Pay can lower an item's margin, raising its points price, while the cash
 * price stays put. Gives the margin tolerance cost, what LE pays to keep the quoted points price,
 * for each item whose points price moved. Undefined when anything else about an item's points
 * changed, or it can no longer take points, as only a moved points price can be kept.
 */
function getLoyaltyMarginTolerances(
  quoted: Array<App.Checkout.LoyaltyBurningPointItem>,
  recalculated: App.Checkout.LoyaltyPointBurnCalculationView,
): Array<LoyaltyMarginTolerance> | undefined {
  const recalculatedByItemId = new Map(recalculated.burnableItems.map(item => [item.itemView.item.itemId, item]))
  const tolerances: Array<LoyaltyMarginTolerance> = []
  for (const item of quoted) {
    const repriced = recalculatedByItemId.get(item.itemId)
    if (
      !repriced?.actualPriceInPoints ||
      repriced.actualPriceInCurrency !== item.priceInCurrency ||
      repriced.itemView.loyalty.productType !== item.productType
    ) {
      return undefined
    }
    if (repriced.actualPriceInPoints === item.priceInPoints) continue
    // Only an item whose points price moved needs a quoted margin to keep, e.g. not an experience
    if (!item.mx) return undefined

    const repricedPointsValue = (item.pointsPaid * item.priceInCurrency) / repriced.actualPriceInPoints
    tolerances.push({
      itemId: item.itemId,
      mx: item.mx,
      cost: Math.max(0, floatify(item.pointsPaidInCurrency - repricedPointsValue)),
    })
  }
  return tolerances
}

export default getLoyaltyMarginTolerances
